// src/components/DietaryIcon.tsx
import React from 'react';
import { GoDotFill } from 'react-icons/go';
import { getPrimaryDietaryType } from '../utils/dietaryUtils';

interface DietaryIconProps {
  item: any;
  variant?: 'dot' | 'overlay' | 'badge';
  size?: 'sm' | 'md' | 'lg';
  showLabel?: boolean;
  className?: string;
}

const DietaryIcon: React.FC<DietaryIconProps> = ({
  item,
  variant = 'dot',
  size = 'md',
  showLabel = false,
  className = ''
}) => {
  const primaryType = getPrimaryDietaryType(item);
  
  // Size configurations for dot and badge variants only
  const sizeConfig = {
    sm: {
      container: 'w-4 h-4',
      icon: 'w-3 h-3',
      badge: 'px-2 py-1 text-xs',
      badgeIcon: 'w-4 h-4'
    },
    md: {
      container: 'w-5 h-5',
      icon: 'w-3 h-3',
      badge: 'px-3 py-1 text-sm',
      badgeIcon: 'w-5 h-5'
    },
    lg: {
      container: 'w-6 h-6',
      icon: 'w-4 h-4',
      badge: 'px-4 py-2 text-base',
      badgeIcon: 'w-6 h-6'
    }
  };

  const config = sizeConfig[size];

  // Get label text
  const getLabel = () => {
    switch (primaryType) {
      case 'vegan':
        return 'Vegan';
      case 'vegetarian':
        return 'Vegetarian';
      case 'non-vegetarian':
        return 'Non-Vegetarian';
      default:
        return 'Non-Vegetarian';
    }
  };

  // Render based on variant
  switch (variant) {
    case 'dot':
      return (
        <div 
          className={`${config.container} rounded-sm flex items-center justify-center border-2 bg-white flex-shrink-0 ${className}`}
          style={{
            borderColor: primaryType === 'non-vegetarian' ? '#dc2626' : '#16a34a' // red border for non-veg, green border for veg/vegan
          }}
          title={getLabel()}
          aria-label={getLabel()}
        >
          <GoDotFill 
            className={config.icon}
            style={{
              color: primaryType === 'non-vegetarian' ? '#dc2626' : '#16a34a' // red dot for non-veg, green dot for veg/vegan
            }}
          />
        </div>
      );

    case 'overlay':
      // Hide overlay icons for veg/vegan - only show simple dots next to title
      return null;

    case 'badge':
      return (
        <div 
          className={`${config.badge} text-white rounded-sm flex items-center gap-1 font-medium border-2 ${className}`}
          style={{
            backgroundColor: primaryType === 'non-vegetarian' ? '#dc2626' : '#16a34a', // red for non-veg, green for veg/vegan
            borderColor: primaryType === 'non-vegetarian' ? '#dc2626' : '#16a34a'
          }}
          title={getLabel()}
          aria-label={getLabel()}
        >
          <GoDotFill 
            className={`${config.badgeIcon} text-white`}
          />
          {showLabel && <span>{getLabel()}</span>}
        </div>
      );

    default:
      return null;
  }
};

export default DietaryIcon;

// Export additional utility components for specific use cases
export const VegDotIcon: React.FC<{ item: any; size?: 'sm' | 'md' | 'lg'; className?: string }> = ({ 
  item, 
  size = 'md', 
  className = '' 
}) => (
  <DietaryIcon item={item} variant="dot" size={size} className={className} />
);

export const DietaryOverlayIcon: React.FC<{ item: any; size?: 'sm' | 'md' | 'lg'; className?: string }> = ({ 
  item, 
  size = 'md', 
  className = '' 
}) => (
  <DietaryIcon item={item} variant="overlay" size={size} className={className} />
);

export const DietaryBadge: React.FC<{ item: any; size?: 'sm' | 'md' | 'lg'; showLabel?: boolean; className?: string }> = ({ 
  item, 
  size = 'md', 
  showLabel = true, 
  className = '' 
}) => (
  <DietaryIcon item={item} variant="badge" size={size} showLabel={showLabel} className={className} />
);
